import type { Trip } from "../types";

/**
 * Resolves whether a given user's location is shared on a trip.
 *
 * Two-level privacy: the per-trip override on the user's `travelParty` entry
 * wins when it's an explicit boolean; `null` (or no entry) means "inherit the
 * account-level default".
 */
export function effectiveTripSharing(
  trip: Pick<Trip, "travelParty">,
  userId: string,
  accountDefault: boolean,
): boolean {
  const member = Object.values(trip.travelParty ?? {}).find((m) => m.userId === userId);
  const override = member?.locationSharingEnabled;
  if (override === true) return true;
  if (override === false) return false;
  return accountDefault;
}

/** Finds the `travelParty` map key for a given user, if present. */
export function findTravelPartyKey(
  trip: Pick<Trip, "travelParty">,
  userId: string,
): string | null {
  for (const [key, member] of Object.entries(trip.travelParty ?? {})) {
    if (member.userId === userId) return key;
  }
  return null;
}

const EARTH_RADIUS_M = 6_371_000;

/** Great-circle distance between two lat/lng points, in metres. */
export function haversineMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Human-friendly distance string, e.g. "120 m" or "3.4 km". */
export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10_000 ? 1 : 0)} km`;
}
